import { makeObservable, observable, action, computed } from 'mobx'
import { get } from 'mobx'
import { BaseItem } from '../base/BaseItem'
import { Recurrence } from '../utils/recurrence'
import { Comments } from '../utils/comments'
import { History } from '../utils/history'
import { Subtasks } from '../utils/subtasks' // Re-used here as "steps"

/**
 * Class representing a Workflow item, extending the BaseItem.
 * Contains additional properties and methods specific to Workflow items.
 *
 * @class Workflow
 * @extends {BaseItem}
 */
export class Workflow extends BaseItem {
	_class = 'Workflow'
	workflow_id

	/**
	 * Creates an instance of Workflow.
	 * @param {string} id - The unique identifier for the Workflow item.
	 */
	constructor(id) {
		super(id)
		this.workflow_id = id
		// In workflows, "steps" replace "subtasks"
		this.steps = new Subtasks(this.full.steps || [])
		this.recurrence = new Recurrence(this.full.recurrence || {})
		this.comments = new Comments(this.full.comments || [])
		this.history = new History(this.full.history || {})

		// Initialize observable properties and actions.
		makeObservable(this, {
			workflow_id: observable,
			steps: observable,
			recurrence: observable,
			comments: observable,
			history: observable,
			setRecurrence: action,
			setComments: action,
			setSteps: action,
			updateFromServerCompact: action,
			updateFromServerFull: action,

			// Workflow specific methods
			priority: computed,
			setPriority: action.bound,

			effortEstimate: computed,
			setEffortEstimate: action.bound,

			platformIds: computed,
			setPlatformIds: action.bound,

			workflow_type: computed,
			setWorkflowType: action.bound
		})
	}

	/**
	 * Sets the workflow steps.
	 * @param {Array} steps - Array representing the workflow steps.
	 */
	setSteps = (steps) => {
		this.steps = new Subtasks(steps)
		this.chgInFull(['steps'], steps || [])
	}

	/**
	 * Sets the recurrence data.
	 * @param {Object} recurrence - The recurrence data.
	 */
	setRecurrence = (recurrence) => {
		this.recurrence = new Recurrence(recurrence)
		this.chgInFull(['recurrence'], recurrence)
	}

	/**
	 * Sets the comments.
	 * @param {Array} comments - Array of comments.
	 */
	setComments = (comments) => {
		this.comments = new Comments(comments)
		this.chgInFull(['comments'], comments)
	}

	/**
	 * Getter for priority. Defaults to 0.
	 */
	get priority() {
		return get(this.full, ['priority'], get(this.compact, ['priority'], 0))
	}

	/**
	 * Sets the priority.
	 * @param {number} val - New priority value.
	 */
	setPriority = (val) => {
		this.chgInFull(['priority'], val)
	}

	/**
	 * Getter for effort estimate. Defaults to 0.
	 */
	get effortEstimate() {
		return get(
			this.full,
			['effortEstimate'],
			get(this.compact, ['effortEstimate'], 0)
		)
	}

	/**
	 * Sets the effort estimate.
	 * @param {number} val - New effort estimate.
	 */
	setEffortEstimate = (val) => {
		this.chgInFull(['effortEstimate'], val)
	}

	/**
	 * Getter for platform IDs.
	 */
	get platformIds() {
		return get(this.full, ['platformIds'], [])
	}

	/**
	 * Sets the platform IDs.
	 * @param {Array} val - Array of platform IDs.
	 */
	setPlatformIds = (val) => {
		this.chgInFull(['platformIds'], val)
	}

	/**
	 * Getter for workflow type.
	 * This replaces the category_id used in sprints.
	 */
	get workflow_type() {
		return get(
			this.full,
			['workflow_type'],
			get(this.compact, ['workflow_type'], '')
		)
	}

	/**
	 * Sets the workflow type.
	 * @param {string} val - The workflow type.
	 */
	setWorkflowType = (val) => {
		this.chgInFull(['workflow_type'], val)
	}

	/**
	 * Calculates the next recurrence.
	 * When there are no recurrences left, it marks the workflow as completed.
	 */
	setRecurrenceNext() {
		// When recurrences are exhausted, mark workflow as completed.
		const onCompletion = () => {
			this.setProgress('completed')
		}
		// Otherwise, mark it as active.
		const onNext = () => {
			this.setProgress('active')
		}
		this.recurrence.calculateNext(onCompletion, onNext)

		// If a new recurrence is set and steps exist, reset steps status to active.
		if (this.recurrence.next && this.steps.list.length > 0) {
			this.steps.resetStatus()
		}
		this.chgInFull(['recurrence', 'next'], this.recurrence.next)
	}

	/**
	 * Updates the compact version of the Workflow from server data.
	 * @param {object} compactJson - Compact data from the server.
	 */
	updateFromServerCompact(compactJson) {
		if (compactJson.recurrence) {
			this.recurrence = new Recurrence(compactJson.recurrence)
		}
		if (compactJson.priority) {
			this.setPriority(compactJson.priority)
		}
		if (compactJson.workflow_type) {
			this.setWorkflowType(compactJson.workflow_type)
		}
	}

	/**
	 * Updates the full version of the Workflow from server data.
	 * @param {object} fullJson - Full data from the server.
	 */
	updateFromServerFull(fullJson) {
		if (fullJson.recurrence) {
			this.recurrence = new Recurrence(fullJson.recurrence)
		}
		if (fullJson.comments) {
			this.comments = new Comments(fullJson.comments)
		}
		if (fullJson.history) {
			this.history = new History(fullJson.history)
		}
		if (fullJson.steps) {
			this.steps = new Subtasks(fullJson.steps)
		}

		if (fullJson.priority) {
			this.setPriority(fullJson.priority)
		}
		if (fullJson.workflow_type) {
			this.setWorkflowType(fullJson.workflow_type)
		}
		if (fullJson.effortEstimate) {
			this.setEffortEstimate(fullJson.effortEstimate)
		}
		if (fullJson.platformIds) {
			this.setPlatformIds(fullJson.platformIds)
		}
	}
}
